import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import Layout from './Layout';
import DraftsTable from './tables/DraftsTable';
import ShareDraftModal from './common/ShareDraftModal';
import ShippingContactModal from './common/ShippingContactModal';
import Pagination from './common/Pagination';
import { formatCurrency } from '../utils/formatters';
import { useToast } from '../contexts/ToastContext';
import './DraftList.css';

// Using Draft type from DraftsTable to ensure consistency
type Draft = {
  draft_id: string;
  draft_number: string;
  status: string;
  subtotal?: number;
  shipping_cost?: number;
  tax_amount?: number;
  total_amount: number;
  notes?: string;
  created_at: string;
  expires_at?: string;
};

interface DraftListResponse {
  items: Draft[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  summary: {
    total_value: number;
    expiring_soon: number;
    pending_approval: number;
  };
}

const DraftList: React.FC = () => {
  // Draft List component
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [summary, setSummary] = useState<any>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<Draft | null>(null);
  const [shareLink, setShareLink] = useState('');
  const [qrEnlarged, setQrEnlarged] = useState(false);

  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Inline filters
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string[]>([]);

  // Available filter options (computed from unfiltered data)
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availablePaymentStatuses, setAvailablePaymentStatuses] = useState<string[]>([]);

  // Format draft number from Q-2025-0911-0001 to DFT-0911-0001
  const formatDraftNumber = (quoteNumber: string) => {
    if (!quoteNumber) return '';
    const parts = quoteNumber.split('-');
    if (parts.length >= 4) {
      return `DFT-${parts[2]}-${parts[3]}`;
    }
    return `DFT-${quoteNumber.slice(-8)}`;
  };

  const fetchDrafts = useCallback(async () => {
    try {
      const params: any = {
        page: currentPage,
        page_size: 10,
        sort_by: sortBy,
        sort_order: sortOrder
      };

      // Add filters if set
      if (statusFilter.length > 0) {
        params.status = statusFilter;
      }
      if (paymentStatusFilter.length > 0) {
        params.payment_status = paymentStatusFilter;
      }

      const response = await api.get<DraftListResponse>('/drafts/', { params });

      setDrafts(response.data.items);
      setTotalPages(response.data.total_pages);
      setSummary(response.data.summary);
      setError('');

      // Compute available options from unfiltered data (only when no filters active)
      if (statusFilter.length === 0 && paymentStatusFilter.length === 0) {
        const statuses = new Set<string>();
        const paymentStatuses = new Set<string>();

        response.data.items.forEach(draft => {
          // Derive status similar to DraftsTable
          if (draft.status === 'CONVERTED') {
            statuses.add('CONVERTED');
          } else if (draft.expires_at) {
            const now = new Date();
            const exp = new Date(draft.expires_at);
            if (exp < now) {
              statuses.add('EXPIRED');
            } else {
              statuses.add('ACTIVE');
            }
          }

          // Derive payment status
          if (draft.status === 'CONVERTED') {
            paymentStatuses.add('PAID');
          } else {
            paymentStatuses.add('NOT_CONVERTED');
          }
        });

        setAvailableStatuses(Array.from(statuses).sort());
        setAvailablePaymentStatuses(Array.from(paymentStatuses).sort());
      }
    } catch (err: any) {
      // Don't show error notification when no drafts found
      console.error('Drafts fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, sortBy, sortOrder, statusFilter, paymentStatusFilter]);

  useEffect(() => {
    if (user) {
      fetchDrafts();
    }
  }, [user, currentPage, fetchDrafts]);
  
  // Track window width for responsive layout
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      'quote': 'bg-primary',
      'pending_approval': 'bg-warning',
      'approved': 'bg-success',
      'rejected': 'bg-danger',
      'expired': 'bg-secondary',
      'converted': 'bg-info'
    };
    
    const statusLabels: { [key: string]: string } = {
      'draft': 'Draft',
      'quote': 'Draft',
      'pending_approval': 'Pending Approval',
      'approved': 'Approved',
      'rejected': 'Rejected',
      'expired': 'Expired',
      'converted': 'Converted to Order'
    };
    
    return (
      <span className={`badge ${statusClasses[status] || 'bg-secondary'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };


  const shareDraft = async (draft: Draft) => {
    // Show contact modal first to collect shipping contact info
    setSelectedDraft(draft);
    setShowContactModal(true);
  };

  const handleContactSaved = async () => {
    // Close contact modal
    setShowContactModal(false);

    // Now create the share link
    if (!selectedDraft) return;

    try {
      const response = await api.post(`/drafts/${selectedDraft.draft_id}/share`, {
        expires_in_hours: 168 // 7 days
      });
      // Use full URL with hostname
      const fullUrl = response.data.share_url.startsWith('http')
        ? response.data.share_url
        : `${window.location.origin}${response.data.share_url}`;
      setShareLink(fullUrl);
      setShowShareModal(true);

      // Auto-copy to clipboard
      await navigator.clipboard.writeText(fullUrl);
    } catch (err) {
      console.error('Failed to share draft:', err);
      showError('Failed to create share link');
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Toggle sort order
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const deleteDraft = async (draft: Draft) => {
    if (!window.confirm(`Are you sure you want to delete draft ${formatDraftNumber(draft.draft_number)}? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/drafts/${draft.draft_id}`);
      await fetchDrafts();
      setError('');
    } catch (err: any) {
      setError('Failed to delete draft. Please try again.');
      console.error('Delete draft error:', err);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading drafts...</span>
            </div>
            <p className="mt-3">Loading drafts...</p>
          </div>
        </div>
      </Layout>
    );
  }
  
  if (!user) {
    return (
      <Layout>
        <div className="container mt-5">
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            Please log in to view your drafts
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '20px 1.5rem'
      }}>
        <div className="row">
          <div className="col-12">
            <div className="mb-4">
              <h1 style={{
                fontSize: '28px',
                fontWeight: '600',
                color: '#2c3e50'
              }}>
                <i className="fas fa-file-alt me-2"></i>Drafts
              </h1>
            </div>
            
            {error && (
              <div className="alert alert-danger alert-dismissible fade show" role="alert">
                <i className="fas fa-exclamation-circle me-2"></i>{error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}

            {drafts.length === 0 ? (
              <div className="card">
                <div className="card-body text-center py-5">
                  <i className="fas fa-file-alt fa-3x text-muted mb-3"></i>
                  <h4>No drafts found</h4>
                  <button
                    className="btn"
                    onClick={() => navigate('/')}
                    style={{
                      backgroundColor: '#4D64C1',
                      color: 'white',
                      border: 'none',
                      padding: '10px 20px',
                      fontSize: '1rem'
                    }}
                  >
                    Go To Dashboard
                  </button>
                </div>
              </div>
            ) : (
              <>
                {(statusFilter.length > 0 || paymentStatusFilter.length > 0) && (
                  <div className="mb-3 d-flex justify-content-end">
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setStatusFilter([]);
                        setPaymentStatusFilter([]);
                      }}
                      style={{ fontSize: '0.875rem', padding: '6px 12px' }}
                    >
                      <i className="fas fa-times me-2"></i>Clear Filters
                    </button>
                  </div>
                )}
                <DraftsTable
                  drafts={drafts}
                  onShare={shareDraft}
                  onCheckout={(draftId: string) => navigate(`/checkout/draft/${draftId}`)}
                  onDelete={deleteDraft}
                  showAllLink={false}
                  compact={false}
                  showHeader={false}
                  showFilters={true}
                  statusFilter={statusFilter}
                  paymentStatusFilter={paymentStatusFilter}
                  onStatusFilterChange={setStatusFilter}
                  onPaymentStatusFilterChange={setPaymentStatusFilter}
                  availableStatuses={availableStatuses}
                  availablePaymentStatuses={availablePaymentStatuses}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ShippingContactModal
        show={showContactModal && !!selectedDraft}
        onClose={() => {
          setShowContactModal(false);
          setSelectedDraft(null);
        }}
        onSuccess={handleContactSaved}
        draftId={selectedDraft?.draft_id || ''}
        draftNumber={selectedDraft ? `DFT-${selectedDraft.draft_number.slice(-8)}` : undefined}
      />

      <ShareDraftModal
        show={showShareModal && !!selectedDraft}
        onClose={() => {
          setShowShareModal(false);
          setQrEnlarged(false);
        }}
        shareLink={shareLink}
        draftNumber={selectedDraft ? `DFT-${selectedDraft.draft_number.slice(-8)}` : undefined}
      />
    </Layout>
  );
};

export default DraftList;